import { ApolloProvider, useReactiveVar } from '@apollo/client'
import { jwtDecode } from 'jwt-decode'
import React, { useEffect, useReducer, useRef } from 'react'
import { BrowserRouter } from 'react-router-dom'
import 'react-toastify/dist/ReactToastify.css'
import { ToastContainer } from 'react-toastify'
import { StyleSheetManager } from 'styled-components'
import { QueryParamProvider } from 'use-query-params'
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6'

import { apolloClient } from '@src/apolloClient'
import { ErrorBoundary } from '@src/components/ErrorBoundary'
import { Helmet } from '@src/components/Helmet'
import { initialiseFulfilmentFilter } from '@src/hooks/useFulfilmentFilter/initialise'
import { fulfilmentFilterReactiveVar } from '@src/hooks/useFulfilmentFilter/utils/reactiveVar'
import { MarketplaceProvider, useMarketplace } from '@src/hooks/useMarketplace'
import { jwtVar } from '@src/models/customer/jwt'
import { buildMarketplaceStructuredData } from '@src/utils/structuredData/structuredData'

import { LoadingSpinner } from '../components/Loaders/LoadingSpinner'
import { CaptureWindowResize } from './CaptureWindowResize'
import { GlobalStyles } from './GlobalStyles'
import { LocalStorageSync } from './LocalStorageSync'
import { PageWrapper } from './PageWrapper'
import { MarketplaceRoutes } from './Routes/MarketplaceRoutes'
import { VendorRoutes } from './Routes/VendorRoutes'
import {
  MarketplaceThemeLoader,
  BusinessThemeLoader,
} from './theme/ThemeLoader'
import isPropValid from '@emotion/is-prop-valid'

import { useLazyCustomerDetailsAndAddressesQuery } from '../hooks/sharedQueries/useCustomerDetailsAndAddressesQuery/useCustomerDetailsAndAddressesQuery'
import { useTranslation } from 'react-i18next'
import { CookieConsentBanner } from '../components/CookieConsentBanner'
import { useCookieConsent } from '../hooks/useCookieConsent/useCookieConsent'
import { initGa4, initGoogleAds, updateConsent } from '../analytics/gtag'
import { initMetaPixel } from '../analytics/meta'
import { setAnalyticsBrand } from '../analytics/superProperties'
import { captureUtmParams, persistUtmParams } from '../analytics/utm'
import {
  grantPostHogConsent,
  identifyPostHog,
  initPostHog,
  revokePostHogConsent,
  setPostHogMarketplaceGroup,
  setPostHogPartnerGroup,
  setPostHogPlatformGroup,
} from '../analytics/posthog'

/** Syncs the PostHog platform + partner + marketplace groups for the current marketplace. */
const syncPostHogGroups = (marketplace: {
  id: string
  name: string
  partnerId?: string | null
  platformId?: string | null
}): void => {
  setPostHogMarketplaceGroup(marketplace.id, {
    name: marketplace.name,
    host: window.location.hostname,
    partner_id: marketplace.partnerId ?? undefined,
    platform_id: marketplace.platformId ?? undefined,
  })
  if (marketplace.partnerId) {
    setPostHogPartnerGroup(marketplace.partnerId, {
      platform_id: marketplace.platformId ?? undefined,
    })
  }
  if (marketplace.platformId) {
    setPostHogPlatformGroup(marketplace.platformId)
  }
}

const FulfilmentFilterInitialiser: React.FC = () => {
  const marketplace = useMarketplace()
  const { isInitialised } = useReactiveVar(fulfilmentFilterReactiveVar)
  // force update the component when the fulfilment filter is initialised as the component is not re-rendered when the reactive var changes
  const [, forceUpdate] = useReducer(x => x + 1, 0)
  const [customerDetailsQuery] = useLazyCustomerDetailsAndAddressesQuery()
  const { i18n } = useTranslation()
  const { consent } = useCookieConsent()
  const trackingInitRef = useRef(false)
  const ga4InitRef = useRef(false)

  useEffect(() => {
    if (marketplace?.ietf) {
      void i18n.changeLanguage(marketplace.ietf)
    }
  }, [marketplace?.ietf])

  // Load GA4 (gtag.js) for this marketplace once its id is known (Consent Mode
  // v2 default denied). We use gtag.js directly rather than GTM to avoid running
  // arbitrary tenant-controlled scripts on our shared multi-tenant domain.
  useEffect(() => {
    if (ga4InitRef.current || !marketplace.ga4Id) return
    ga4InitRef.current = true
    initGa4(marketplace.ga4Id)
    // Shares the gtag instance GA4 just loaded, so it has to follow it.
    initGoogleAds(marketplace.googleAdsId, marketplace.googleAdsPurchaseLabel)
  }, [
    marketplace.ga4Id,
    marketplace.googleAdsId,
    marketplace.googleAdsPurchaseLabel,
  ])

  // Capture UTM parameters from the landing URL once, so campaign attribution
  // survives navigation through the ordering journey (RED-10693).
  useEffect(() => {
    captureUtmParams()
  }, [])

  // Tag every analytics event with the active marketplace brand key.
  useEffect(() => {
    setAnalyticsBrand(marketplace?.key)
  }, [marketplace?.key])

  // initialise the fulfilment filters using the marketplace
  useEffect(() => {
    const initialise = async () => {
      const token = jwtVar()

      // fetch customer details and if available, use default address to initialise fulfilment filter
      let defaultAddressId = null
      if (token) {
        try {
          const { data: customerDetails } = await customerDetailsQuery()
          defaultAddressId =
            customerDetails?.customerDetails.deliveryAddresses.find(
              address => address.default
            )?.id || null
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(
            err,
            'Failed to fetch customer details. Defaulting to everywhere.'
          )
        }
      }
      initialiseFulfilmentFilter(marketplace, defaultAddressId, forceUpdate)
    }
    if (!isInitialised) {
      void initialise()
    } // eslint-disable-next-line react-hooks/exhaustive-deps -- initialise is recreated each render; only re-run when isInitialised changes
  }, [customerDetailsQuery, isInitialised])

  useEffect(() => {
    // Starts cookieless, so rejecting and undecided visitors are still counted.
    initPostHog()
    syncPostHogGroups(marketplace)

    if (consent !== 'accepted') {
      if (trackingInitRef.current) {
        // Trackers were previously initialized - disable them
        updateConsent(false)
        if (marketplace.facebookPixelId && (window as any).fbq) {
          ;(window as any).fbq('consent', 'revoke')
        }
        revokePostHogConsent()
        trackingInitRef.current = false
      }
      return
    }
    if (trackingInitRef.current) return
    trackingInitRef.current = true

    // Consent Mode v2: unlock GA4 + Google Ads storage now that the customer
    // has opted in.
    updateConsent(true)

    // Grant FB pixel consent if previously revoked
    if (marketplace.facebookPixelId && (window as any).fbq) {
      ;(window as any).fbq('consent', 'grant')
    }

    const token = jwtVar()

    // run on first render - regardless of fulfilment filter initialisation
    let customerId
    try {
      customerId =
        (token && jwtDecode<{ customer_id: string }>(token)?.customer_id) ||
        undefined
    } catch {
      customerId = undefined
    }

    // PostHog was already capturing cookielessly — unlock storage and replay.
    grantPostHogConsent()
    persistUtmParams()
    if (customerId) {
      identifyPostHog(customerId)
    }

    // The pixel library injects Meta's script before it validates the id, so
    // without this guard a marketplace with no pixel still contacts Meta.
    if (marketplace.facebookPixelId) {
      initMetaPixel(marketplace.facebookPixelId, customerId)
    }
  }, [marketplace.facebookPixelId, marketplace.ga4Id, consent])

  // Not memoised: useMarketplace returns a fresh object each call, so any dependency array would always miss.
  const marketplaceStructuredData = buildMarketplaceStructuredData(
    marketplace,
    window.location.origin
  )

  if (!isInitialised) return <LoadingSpinner />

  if (marketplace.businessId) {
    return (
      <>
        <Helmet
          faviconImage={marketplace.faviconImage}
          keywords={marketplace.metaKeywords}
          structuredData={marketplaceStructuredData}
        />
        <BusinessThemeLoader businessId={marketplace.businessId}>
          <PageWrapper>
            <BrowserRouter>
              <VendorRoutes />
            </BrowserRouter>
          </PageWrapper>
          <CookieConsentBanner />
        </BusinessThemeLoader>
      </>
    )
  }

  // TODO: work out why GlobalStyles and QueryParamProvider are only used in this response and not the one above
  return (
    <>
      <Helmet
        title={marketplace.title || marketplace.name}
        description={marketplace.metaDescription}
        keywords={marketplace.metaKeywords}
        faviconImage={marketplace.faviconImage}
        storeURLApple={marketplace.storeURLApple}
        structuredData={marketplaceStructuredData}
      />
      <MarketplaceThemeLoader marketplaceId={marketplace.id}>
        <GlobalStyles />
        <BrowserRouter>
          <QueryParamProvider adapter={ReactRouter6Adapter}>
            <PageWrapper>
              <MarketplaceRoutes />
            </PageWrapper>
          </QueryParamProvider>
        </BrowserRouter>
        <CookieConsentBanner />
      </MarketplaceThemeLoader>
    </>
  )
}

// This implements the default behavior from styled-components v5
function shouldForwardProp(propName: any, target: any) {
  if (typeof target === 'string') {
    // For HTML elements, forward the prop if it is a valid HTML attribute
    return isPropValid(propName)
  }
  // For other elements, forward all props
  return true
}

export const App: React.FC = () => (
  <ErrorBoundary>
    <ToastContainer
      newestOnTop
      hideProgressBar
      autoClose={4000}
      draggable={false}
    />
    <StyleSheetManager shouldForwardProp={shouldForwardProp}>
      <ApolloProvider client={apolloClient}>
        <CaptureWindowResize>
          <LocalStorageSync>
            <MarketplaceProvider>
              <FulfilmentFilterInitialiser />
            </MarketplaceProvider>
          </LocalStorageSync>
        </CaptureWindowResize>
      </ApolloProvider>
    </StyleSheetManager>
  </ErrorBoundary>
)
